import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import App from './App.vue'
import router from './router'
import './styles/themes.css'

/**
 * 2026-06-02 — SSO hash consumption MUST happen before the router runs.
 *
 * The router's `beforeEach` calls `readToken()` which checks
 * `window.location.hash` for `#token=...&tenant_id=...&email=...`
 * (handoff payload from console's "Open editor →" link). If the hash
 * isn't consumed and persisted into localStorage BEFORE the router
 * decides whether to bounce to /login, the user gets bounced even
 * though the credentials were right there in the URL.
 *
 * Previously this lived in `stores/auth.ts::mirrorOnBoot()` at module
 * top-level. But `stores/auth.ts` is lazy-imported through AppLayout,
 * which only loads AFTER the router resolves — too late. Pulling the
 * consumption up here, into main.ts, guarantees it runs in the very
 * first synchronous block of the app, before `app.use(router)`.
 */
;(function consumeSsoHash() {
  try {
    const hash = window.location.hash || ''
    if (!hash.includes('token=')) return
    const params = new URLSearchParams(hash.replace(/^#/, ''))
    const token    = params.get('token')    || ''
    const tenantId = params.get('tenant_id') || ''
    const email    = params.get('email')    || ''
    if (token) {
      localStorage.setItem('must_token',    token)
      localStorage.setItem('mustapi_token', token)
    }
    if (tenantId) {
      localStorage.setItem('must_tenant_id',    tenantId)
      localStorage.setItem('mustapi_tenant_id', tenantId)
    }
    if (email) {
      localStorage.setItem('mustapi_email', email)
    }
    // Strip the hash so reload / share / screenshot doesn't leak it
    // and so the URL bar settles into a clean state.
    history.replaceState(null, '', window.location.pathname + window.location.search)
  } catch { /* localStorage unavailable, old browser, etc. */ }
})()

// Restore saved theme before app mounts
const savedTheme = localStorage.getItem('must_theme') ?? 'dark'
document.documentElement.setAttribute('data-theme', savedTheme)

const app = createApp(App)

// Register all Element Plus icons globally
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

app.use(createPinia())
app.use(router)
app.use(ElementPlus)
app.mount('#app')
